/**
 * Combined provider — mounts `QueryClientProvider` + the typed tRPC provider
 * in the right order with one component (P2-21).
 *
 * Without this wrapper every app would have to nest the two providers
 * manually and remember the ordering (TanStack Query outer, tRPC inner) and
 * the prop pairing (`{ client, queryClient }` on tRPC's provider).
 *
 * Generic over `TRouter` so the `trpc` instance, the `client`, and the
 * resulting context types are all bound to the same `AppRouter`.
 */
import * as React from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider, type Persister } from '@tanstack/react-query-persist-client';
import { forgetQueries, forgetSignedOutAnswer, persistFilters } from './never-persist.js';
import { seedWorkspaceFromRestoredCache, useCatchUpWithWorkspace } from './workspace-change.js';
import { beginRestoreCheck } from './restore-check.js';
import type { CreateTRPCClient } from '@trpc/client';
import type { AnyTRPCRouter } from '@trpc/server';
import type { PosTrpcReact } from './create.js';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export interface PosTrpcProviderProps<TRouter extends AnyTRPCRouter> {
  /** Typed instance from `createPosTrpcReact<AppRouter>()`. */
  trpc: PosTrpcReact<TRouter>;
  /** tRPC client created via `createPosReactTrpcClient(trpc, opts)`. */
  client: CreateTRPCClient<TRouter>;
  /** TanStack Query client; create with `createPosQueryClient()`. */
  queryClient: QueryClient;
  /**
   * Optional IndexedDB-backed cache persister (ADR-0056 Layer 1). When
   * present, queries + paused mutations survive across reloads and brief
   * offline windows. Build one with `createOfflinePersister(<dbName>)` from
   * `@mangrove/ui`. Pass `null` (or omit) to disable persistence — used by
   * customer-terminal where the device is shared between guests.
   */
  persister?: Persister | null;
  /**
   * Optional cache-buster string for the IndexedDB persister. When it changes
   * between loads, the persisted cache is DISCARDED on restore (TanStack
   * Query `persistOptions.buster`). Key it to the identity/impersonation
   * context — e.g. the active impersonation token — so switching INTO (or
   * between, or out of) an impersonated tenant never rehydrates the previous
   * tenant's cached data (the cross-tenant cache-bleed: a stale sidebar that
   * hides modules the new tenant actually has). Omit for a stable cache.
   */
  persistBuster?: string;
  /**
   * tRPC procedure prefixes whose results must never reach the disk, e.g.
   * `['ops.vault.']`. Filtered out of every persisted write, and purged from
   * anything restored from an older cache (see `never-persist.ts`).
   */
  neverPersist?: readonly string[];
  /**
   * #950: after a restore, hold the saved copy as current and let the live
   * stream's first hello say what changed since (restore-check.ts), instead of
   * refetching everything. The app must also mount `useTenantEventStream` and
   * a fallback for when no stream takes the check (HQ: RefreshAfterRestore).
   */
  checkOnRestore?: boolean;
  children: React.ReactNode;
}

type ProviderShape<TRouter extends AnyTRPCRouter> = {
  Provider: React.ComponentType<{
    client: CreateTRPCClient<TRouter>;
    queryClient: QueryClient;
    children: React.ReactNode;
  }>;
};

export function PosTrpcProvider<TRouter extends AnyTRPCRouter>({
  trpc,
  client,
  queryClient,
  persister,
  persistBuster,
  neverPersist,
  checkOnRestore = false,
  children,
}: PosTrpcProviderProps<TRouter>): React.JSX.Element {
  // `CreateTRPCReact` is a `ProtectedIntersection` whose union shape hides
  // `.Provider` from direct property access (the union admits a string-literal
  // error type). The Provider does exist at runtime; cast `trpc` as a whole
  // so the property access compiles.
  const TRPCProvider = (trpc as unknown as ProviderShape<TRouter>).Provider;
  // #803: when this tab's workspace was switched elsewhere, catch up (reload
  // into the workspace the session is in). See workspace-change.ts.
  useCatchUpWithWorkspace(queryClient, persister);

  if (persister) {
    // ADR-0056 Layer 1 — IndexedDB-persisted cache + paused-mutation queue.
    // `onSuccess` runs after the persisted cache is rehydrated; that's where
    // we resume any mutations that were paused offline (online/offline events
    // also resume them via TanStack Query's built-in network-status listener).
    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: ONE_DAY_MS,
          buster: persistBuster,
          // Always filtered: besides an app's own never-persist prefixes, a
          // signed-out "who am I" is never written (#840).
          dehydrateOptions: persistFilters(neverPersist ?? []),
        }}
        onSuccess={() => {
          // An older cache may hold what must not be on disk: drop it now, and
          // the next write (filtered) leaves the disk without it.
          if (neverPersist?.length) forgetQueries(queryClient, neverPersist);
          // #840: a signed-out answer restored from an older cache is dropped,
          // so the guard asks the server instead of trusting it.
          forgetSignedOutAnswer(queryClient);
          // #823: the page now draws the restored workspace, so its calls say
          // so from the first one, including the paused writes resumed next.
          seedWorkspaceFromRestoredCache(queryClient);
          // #950: before any screen mounts and refetches on age.
          if (checkOnRestore) beginRestoreCheck(queryClient);
          void queryClient.resumePausedMutations();
        }}
      >
        <TRPCProvider client={client} queryClient={queryClient}>
          {children}
        </TRPCProvider>
      </PersistQueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider client={client} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
